import { CONTENT_TYPE, HTTP_METHOD } from './const';
import type { ResourceData } from 'framework/crud';
import { RequestOptions } from './xhr';
import type { AuthHeader } from './api.types';

export const isAbortError = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError';

/**
 * Method to handle specified responses (e.g. authorization rejection or access token expiration)
 * @param {Promise} response -- response got from a fetch() call
 * @returns {Promise}
 */
export const handleResponse = async (response: Response) => {
  if (response.ok || (response.status >= 200 && response.status < 300)) {
    return Promise.resolve(response);
  }

  return Promise.reject(response);
};

type RequestParams = {
  method: HTTP_METHOD;
  data: ResourceData;
  contentType: CONTENT_TYPE;
  eventTarget: Nullable<AbortSignal>;
};

/**
 * Method for creating initializing object
 * @param headers -- request headers that will be extended
 * @param params -- object with other parameters
 * @returns {Object}
 */
export const initializeRequest = (
  headers: Headers,
  { method, data, contentType, eventTarget }: RequestParams,
): RequestOptions => {
  const init: RequestOptions = {
    method,
    headers,
    cache: 'default',
  };

  if (method !== HTTP_METHOD.GET && data) {
    init.body = contentType === CONTENT_TYPE.JSON ? JSON.stringify(data) : data;
  }

  if (eventTarget instanceof AbortSignal) {
    init.signal = eventTarget;
  }

  return init;
};

/**
 * Method that creates headers object
 * @param headers -- array of [name: value] pairs
 * @param contentType
 * @returns {Headers}
 */
export const createHeaders: (headers: AuthHeader[], contentType: CONTENT_TYPE) => Headers = (
  headers = [],
  contentType = '',
) => {
  const requestHeaders = new Headers();

  if (contentType) {
    requestHeaders.append('Content-Type', contentType);
  }

  if (headers && headers.length) {
    headers.forEach(header => {
      requestHeaders.append(header.name, header.value);
    });
  }

  return requestHeaders;
};

/**
 * Method for safe execution of async functions
 * @param fn -- async function to be executed
 * @returns {Promise<[T | null, boolean]>} -- tuple of result or null and error flag
 */
export const safe = async <T>(fn: () => Promise<T>): Promise<[T | null, boolean]> => {
  try {
    const result = await fn();
    return [result, false];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error in safe execution:', error);
    return [null, true];
  }
};
